import { z } from 'zod';
import { Project, TimeEntry } from '../../../types';
import { getWeekNumber, weekToDateRange } from '../../../lib/utils';
import { minutesToTime, timeToMinutes, MIN_DAILY_HOURS, DEFAULT_START_HOUR } from '../../../lib/timeEntryHelpers';

// Quick Hours mode validates `hours`; From/To mode validates `fromHour`/`toHour`.
// Both are present in the schema (optional) and cross-checked in onSubmit based on `mode`.
export const editSchema = z.object({
  mode: z.enum(['quick', 'range']),
  projectId: z.string().min(1, 'Select a project'),
  phaseId: z.string().min(1, 'Select a phase'),
  jobTitleId: z.string().min(1, 'Select a job title'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date'),
  hours: z.coerce.number<number | string>().positive().max(12).optional(),
  fromHour: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time').optional(),
  toHour: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time').optional(),
  notes: z.string().min(1, 'Notes are required'),
}).superRefine((data, ctx) => {
  if (data.mode === 'quick' && !data.hours) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter hours worked', path: ['hours'] });
  }
  if (data.mode === 'range') {
    if (!data.fromHour) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a start time', path: ['fromHour'] });
    if (!data.toHour) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter an end time', path: ['toHour'] });
    // Midnight-aware: toHour <= fromHour is valid (an overnight range, e.g. 22:00 -> 02:00).
    // Only an identical fromHour/toHour is invalid (zero duration).
    if (data.fromHour && data.toHour && data.fromHour === data.toHour) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Start and end time cannot be the same', path: ['toHour'] });
    }
  }
});

// ─── Note guardrails ───────────────────────────────────────────────────────
// Same rules as the "Log Hours" (new entry) page — kept in sync here so
// editing an entry can't be used to slip past them. Notes are free text, so
// we normalize before matching: lowercase, strip punctuation/hyphens/periods
// down to spaces, collapse whitespace. This lets "internal-meeting",
// "Internal   Meeting", and "internalmeeting" (no space at all) all match
// without also matching unrelated words like "international".
export const INTERNAL_MEETING_ERROR = "Internal meetings belong under Non-Project Related, not a project entry.";
export const PERSONAL_TRAINING_ERROR = "Personal Training belongs under Non-Project Related, not a project entry.";

function normalizeNote(note: string): string {
  return note
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function containsInternalMeeting(note: string): boolean {
  const n = normalizeNote(note);
  // Matches "internal meeting(s)" and the abbreviation "int meeting(s)"
  // (e.g. "int. meeting" once punctuation is stripped), with or without a
  // space between the two words ("internalmeeting"), but not "international
  // meeting" since \bint\b requires "int" as its own token.
  return /\b(internal|int)\s?meetings?\b/.test(n);
}

export function containsTraining(note: string): boolean {
  const n = normalizeNote(note);
  return /\btrainings?\b/.test(n);
}

export function getPhaseName(projects: Project[], entry: Pick<TimeEntry, 'projectId' | 'phaseId'>): string | null {
  return projects.find((p) => p.id === entry.projectId)?.type?.phases
    .find((ph) => ph.id === entry.phaseId)?.name ?? null;
}

/** Resolves inactive project status (FROZEN or CLOSED), or null if active / not found. */
export function getEntryProjectInactiveStatus(
  entry: { projectId?: string; project?: { id?: string; status?: string } },
  projects?: Project[],
): 'FROZEN' | 'CLOSED' | null {
  const status = getCurrentProjectStatus(entry, projects);
  if (status === 'FROZEN') return 'FROZEN';
  if (status === 'CLOSED') return 'CLOSED';
  return null;
}

function getCurrentProjectStatus(
  entry: { projectId?: string; project?: { id?: string; status?: string } },
  projects?: Project[],
): string | undefined {
  const projectId = entry.projectId ?? entry.project?.id;
  return projects?.find((project) => project.id === projectId)?.status ?? entry.project?.status;
}

/** True when the entry's project is in FROZEN status. */
export function isEntryProjectFrozen(
  entry: { projectId?: string; project?: { id?: string; status?: string } },
  projects?: Project[],
): boolean {
  return getCurrentProjectStatus(entry, projects) === 'FROZEN';
}

/** True when the entry's project is in CLOSED status. */
export function isEntryProjectClosed(
  entry: { projectId?: string; project?: { id?: string; status?: string } },
  projects?: Project[],
): boolean {
  return getCurrentProjectStatus(entry, projects) === 'CLOSED';
}

/** True when the entry's project is in FROZEN or CLOSED status. */
export function isEntryProjectLocked(
  entry: { projectId?: string; project?: { id?: string; status?: string } },
  projects?: Project[],
): boolean {
  return getEntryProjectInactiveStatus(entry, projects) !== null;
}

/** True when the (possibly edited) job title differs from the employee's own default. */
export function isDifferentJobTitle(jobTitleId: string, defaultJobTitleId: string): boolean {
  return !!jobTitleId && !!defaultJobTitleId && jobTitleId !== defaultJobTitleId;
}

// ─── Day finalization (FinalizeDayDialog) ───────────────────────────────────

/** API times come back as either 'HH:MM' or a full ISO timestamp. */
export const hhmm = (t: string) => (t?.includes?.('T') ? t.slice(11, 16) : t);

/** Keeps fromHour, moves toHour so the entry lasts `hours`. */
export const rangeFor = (fromHour: string, hours: number) => ({
  fromHour,
  toHour: minutesToTime(timeToMinutes(fromHour) + Math.round(hours * 60)),
});

/** True when a day's hours land exactly on the required daily total. */
export const isDayComplete = (total: number) => Math.round(total * 100) === MIN_DAILY_HOURS * 100;

export interface DayLayoutRow {
  id: string;
  fromHour: string;
  toHour: string;
  hours: number;
  editable: boolean;
}

/**
 * Re-lays a day's entries back to back after their durations changed. Editing
 * only an entry's end time makes a longer entry run into whatever starts next,
 * so every editable entry is instead re-placed where the previous one ends —
 * the same way entries are stacked when they're first logged.
 *
 * Rows keep their chronological order. Entries an approver already has can't be
 * moved, so they stay put as fixed blocks and editable entries are placed in
 * the gaps around them: an entry that would run into one is pushed past it
 * rather than laid on top of it.
 */
export function layOutDay(rows: DayLayoutRow[]): {
  placed: Record<string, { fromHour: string; toHour: string }>;
  endsAt: string;
} {
  const ordered = [...rows].sort((a, b) => timeToMinutes(a.fromHour) - timeToMinutes(b.fromHour));
  const blocks = ordered
    .filter((r) => !r.editable)
    .map((r) => ({ start: timeToMinutes(r.fromHour), end: timeToMinutes(r.toHour) }));

  const placed: Record<string, { fromHour: string; toHour: string }> = {};
  let cursor = timeToMinutes(ordered[0]?.fromHour ?? DEFAULT_START_HOUR);

  for (const row of ordered) {
    if (!row.editable) {
      placed[row.id] = { fromHour: row.fromHour, toHour: row.toHour };
      cursor = Math.max(cursor, timeToMinutes(row.toHour));
      continue;
    }

    // Slide past any fixed block this entry would land on. Each pass moves the
    // start strictly forward, so at most one pass per block can be needed.
    const length = Math.round(row.hours * 60);
    let start = cursor;
    for (let i = 0; i <= blocks.length; i++) {
      const blocker = blocks.find((b) => b.start < start + length && b.end > start);
      if (!blocker) break;
      start = blocker.end;
    }

    placed[row.id] = rangeFor(minutesToTime(start), row.hours);
    cursor = Math.max(cursor, start + length);
  }
  return { placed, endsAt: minutesToTime(cursor) };
}

// Same orange marker used on the "Log Hours" screen for a job title that
// differs from the employee's default — kept in sync with NewEntryPage/utils.ts.
export const DIFFERENT_JOB_TITLE_CLASS = 'bg-orange-50 dark:bg-orange-950/30 border-2 border-orange-300 dark:border-orange-800 rounded-md p-3';

// ─── Week grouping & totals (ListView) ──────────────────────────────────────

/** Hours that count toward a day/week total; REJECTED entries don't. */
export const sumEntryHours = (entries: Pick<TimeEntry, 'hours' | 'status'>[]) =>
  entries.filter((e) => e.status !== 'REJECTED').reduce((sum, e) => sum + e.hours, 0);

/** date (YYYY-MM-DD) -> counted hours. */
export function totalsByDay(entries: Pick<TimeEntry, 'date' | 'hours' | 'status'>[]): Record<string, number> {
  const byDay: Record<string, number> = {};
  for (const e of entries) {
    if (e.status === 'REJECTED') continue;
    const d = e.date.slice(0, 10);
    byDay[d] = (byDay[d] ?? 0) + e.hours;
  }
  return byDay;
}

/**
 * Groups entries by "YYYY-WW" then date. Keyed by year as well as week because
 * getWeekNumber restarts at Jan 1, so a week straddling New Year is two groups.
 */
export function groupEntriesByWeek<T extends Pick<TimeEntry, 'date'>>(entries: T[]): Record<string, Record<string, T[]>> {
  const grouped: Record<string, Record<string, T[]>> = {};
  for (const entry of entries) {
    const dateStr = entry.date.slice(0, 10);
    const key = `${dateStr.slice(0, 4)}-${String(getWeekNumber(dateStr)).padStart(2, '0')}`;
    if (!grouped[key]) grouped[key] = {};
    (grouped[key][dateStr] ??= []).push(entry);
  }
  return grouped;
}

/** Week keys, newest first. */
export const sortWeekKeys = (grouped: Record<string, unknown>) =>
  Object.keys(grouped).sort((a, b) => b.localeCompare(a));

/** Date range of a "YYYY-WW" group; the year's last week stops at Dec 31 (the rest is next year's week 1). */
export function weekKeyRange(key: string): { from: string; to: string } {
  const year = Number(key.slice(0, 4));
  const { from, to } = weekToDateRange(year, Number(key.slice(5)));
  const yearEnd = `${year}-12-31`;
  return { from, to: to < yearEnd ? to : yearEnd };
}
